import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { IMAGES, statusColor } from "@/data/site";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";
import { Loader2, MapPin, Ruler, Zap, Building2, FileDown, ArrowLeft } from "lucide-react";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/projects/${slug}`).then((r) => setP(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useSeo(p ? `${p.name} | 12 Stone Development Group` : "Project | 12 Stone", p?.summary);

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4">
      <p className="text-muted-foreground">Project not found.</p>
      <Button asChild variant="outline"><Link to="/projects">Back to Projects</Link></Button>
    </div>
  );
  if (!p) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const img = (u) => (u.startsWith("http") ? u : fileUrl(u));
  const heroImg = p.images?.[0] ? img(p.images[0]) : IMAGES.aerialLand;
  const facts = [
    { icon: MapPin, label: "Location", value: p.location },
    { icon: Ruler, label: "Acreage", value: p.acreage },
    { icon: Building2, label: "Development Type", value: p.development_type },
    { icon: Zap, label: "Power Potential", value: p.power_potential },
  ].filter((f) => f.value);

  return (
    <div>
      <section className="relative min-h-[60vh] flex items-end overflow-hidden pt-24">
        <img src={heroImg} alt={p.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/80 to-primary/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pb-12 w-full">
          <Link to="/projects" className="inline-flex items-center text-sm text-muted-foreground hover:text-accent mb-5"><ArrowLeft className="h-4 w-4 mr-1.5" /> All projects</Link>
          <div className="flex gap-2 mb-4">
            <span className={`font-mono text-xs px-3 py-1 rounded-full border ${statusColor(p.status)}`}>{p.status}</span>
            <span className="font-mono text-xs px-3 py-1 rounded-full border border-border bg-white/5 text-gray-300">{p.category}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-6xl font-extrabold text-white text-balance">{p.name}</h1>
          <p className="mt-4 text-lg text-gray-300 max-w-2xl">{p.summary}</p>
        </div>
      </section>

      <section className="border-y border-border bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 lg:grid-cols-4 divide-x divide-border">
          {facts.map((f, i) => (
            <div key={i} className="px-5 py-7">
              <f.icon className="h-5 w-5 text-primary mb-3" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{f.label}</div>
              <div className="text-sm text-foreground mt-1 font-medium">{f.value}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-10">
          <Reveal>
            <h2 className="font-display text-2xl font-bold text-foreground mb-4">Overview</h2>
            <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{p.description}</p>
            {p.utility_info && (
              <div className="mt-6 rounded-lg border border-border bg-card p-5">
                <div className="font-mono text-xs uppercase tracking-widest text-primary mb-2">Utility Information</div>
                <p className="text-sm text-muted-foreground">{p.utility_info}</p>
              </div>
            )}
          </Reveal>

          {p.images?.length > 1 && (
            <Reveal>
              <h2 className="font-display text-2xl font-bold text-foreground mb-4">Gallery</h2>
              <div className="grid grid-cols-2 gap-4">
                {p.images.slice(1).map((u, i) => (
                  <img key={i} src={img(u)} alt="" className="rounded-lg border border-border w-full aspect-[4/3] object-cover" />
                ))}
              </div>
            </Reveal>
          )}

          {p.map_embed && (
            <Reveal>
              <h2 className="font-display text-2xl font-bold text-foreground mb-4">Map</h2>
              <div className="rounded-lg overflow-hidden border border-border aspect-video" dangerouslySetInnerHTML={{ __html: p.map_embed }} />
            </Reveal>
          )}

          {p.documents?.length > 0 && (
            <Reveal>
              <h2 className="font-display text-2xl font-bold text-foreground mb-4">Downloads</h2>
              <div className="space-y-2">
                {p.documents.map((d, i) => (
                  <a key={i} href={fileUrl(d.path)} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:border-primary/40 transition-colors">
                    <FileDown className="h-5 w-5 text-primary" /><span className="text-sm text-foreground">{d.name}</span>
                  </a>
                ))}
              </div>
            </Reveal>
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-24 rounded-xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-semibold text-foreground">Project Inquiry</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-5">Request more information about this opportunity.</p>
            <LeadForm leadType="partner" defaultCategory={`Project: ${p.name}`} submitLabel="SEND INQUIRY" testId="project-inquiry" compact />
          </div>
        </aside>
      </section>
    </div>
  );
}
